import type {
  CaptureStage,
  FacadeElevation,
  MeasureCategory,
  PhotoEvidence,
  PhotoIntegrity,
} from "../api/types.js";

/**
 * How much a photograph is worth as located evidence, in one word.
 *
 * This is the product's whole argument, so it is worth being exact about what
 * is being decided here and what is not. The API already derives the facts:
 * `positionSource` is computed by the database from whether an override was
 * recorded, and `stamp.positionIsMeasured` is computed from that plus the fix
 * quality. Nothing below re-derives either. What this module adds is the
 * vocabulary — a word, a shape and a sentence per state — because the screen
 * has to survive being printed in black and white and being read by somebody
 * who cannot tell the amber row from the red one.
 *
 * The five states are deliberately not a severity scale with a hole in it.
 * "Set by hand" is not a worse fix; it is not a fix at all, and it is the only
 * one of the five that says something about a person rather than a receiver. It
 * gets a different shape for that reason, not a darker colour.
 */
export type PositionStanding =
  | "hand-set"
  | "measured"
  | "degraded"
  | "carried"
  | "absent";

/**
 * The word. Never omitted, never replaced by a colour, and deliberately chosen
 * so no two of them share a stem: a reader skimming a column of these must be
 * able to tell them apart at a glance and in monochrome.
 */
export const POSITION_WORD: Record<PositionStanding, string> = {
  "hand-set": "Set by hand",
  measured: "Measured on site",
  degraded: "Degraded fix",
  carried: "Carried fix",
  absent: "No fix",
};

/**
 * The shape, as a second channel that survives a photocopier.
 *
 * A filled circle for the one state that is a proper measurement, hollowing out
 * as the fix gets worse, and a triangle — a different shape entirely — for the
 * position a person typed in. Marked `aria-hidden` where it is rendered, since
 * a screen reader gets the word.
 */
export const POSITION_MARK: Record<PositionStanding, string> = {
  "hand-set": "▲",
  measured: "●",
  degraded: "◐",
  carried: "◔",
  absent: "○",
};

/** What the state actually means for somebody relying on the photograph. */
export const POSITION_MEANING: Record<PositionStanding, string> = {
  "hand-set":
    "A person typed this position in. The receiver did not measure it, so this " +
    "photograph is not a location record within the meaning of PAS 2030:2023 §6.11.",
  measured:
    "The receiver took this fix at the moment of capture, at an accuracy the " +
    "capture settings accepted.",
  degraded:
    "The receiver took a fix at capture, but a poor one. Read the accuracy " +
    "before relying on the coordinate.",
  carried:
    "No fix was available at capture, so the last known position is shown. It " +
    "was measured somewhere else, at some earlier time.",
  absent: "The receiver measured no position at all. Nothing is claimed here.",
};

/**
 * Whether this state can stand as the location record §6.11 asks for.
 *
 * Only one of the five can, and the answer comes from the API's own
 * `positionIsMeasured` rather than from a second reading of the columns — see
 * `standingOf` below.
 */
export const POSITION_IS_EVIDENCE: Record<PositionStanding, boolean> = {
  "hand-set": false,
  measured: true,
  degraded: false,
  carried: false,
  absent: false,
};

/**
 * The standing of one photograph.
 *
 * The override test comes first and is unconditional. There is no branch here
 * in which a photograph carrying an override can come out as anything but
 * "hand-set" — which is the point, and is what `evidence.test.tsx` breaks to
 * prove the test can fail.
 */
export function standingOf(photo: PhotoEvidence): PositionStanding {
  if (photo.positionSource === "manual-override") return "hand-set";
  switch (photo.fixQuality) {
    case "measured":
      return "measured";
    case "degraded":
      return "degraded";
    case "last-known":
      return "carried";
    case "none":
      return "absent";
  }
}

/**
 * The class the row and the badge carry.
 *
 * Kept alongside the word rather than instead of it. `critical` for the two
 * states that would mislead somebody who only glanced — a hand-set position and
 * a fix that was never taken — `approaching` for the two that are real but
 * qualified, and `tracking` for the one that is sound.
 */
export function standingTone(standing: PositionStanding): string {
  if (standing === "hand-set" || standing === "absent") return "critical";
  if (standing === "degraded" || standing === "carried") return "approaching";
  return "tracking";
}

/**
 * The integrity verdict in one word, once it has been asked for.
 *
 * `unchecked` is a state in its own right and must not read as "fine". A
 * photograph nobody has verified is not a verified photograph, and a screen
 * that showed nothing until the answer arrived would let a reader assume the
 * silence meant yes.
 */
export type IntegrityStanding = "unchecked" | "intact" | "failed";

export const INTEGRITY_WORD: Record<IntegrityStanding, string> = {
  unchecked: "Not checked",
  intact: "Intact",
  failed: "Integrity FAILED",
};

export const INTEGRITY_MARK: Record<IntegrityStanding, string> = {
  unchecked: "?",
  intact: "✓",
  failed: "✕",
};

export function integrityStanding(
  result: PhotoIntegrity | null,
): IntegrityStanding {
  if (result === null) return "unchecked";
  // The API sends a verdict per half and a single `ok` over the pair. Reading
  // the pair rather than either half is what stops a screen showing a tick
  // because whichever half it happened to look at passed.
  return result.ok ? "intact" : "failed";
}

export function integrityTone(standing: IntegrityStanding): string {
  if (standing === "failed") return "breached";
  if (standing === "intact") return "tracking";
  return "approaching";
}

/** Which half or halves failed, in words, for the line under the verdict. */
export function integrityFailures(result: PhotoIntegrity): string[] {
  const failures: string[] = [];
  if (!result.original.ok) {
    failures.push(`The original file: ${result.original.reason}`);
  }
  if (!result.stamped.ok) {
    failures.push(`The stamped file: ${result.stamped.reason}`);
  }
  return failures;
}

/**
 * The stage a shot was taken at.
 *
 * `pre-cover` is spelled out rather than title-cased, because it is the one
 * that cannot be retaken: after it the work is behind render or plaster, and a
 * missing one at an audit cannot be fixed at any price.
 */
export const STAGE_LABEL: Record<CaptureStage, string> = {
  "pre-installation": "Before installation",
  "in-progress": "Work in progress",
  "pre-cover": "Before covering up",
  "post-cover": "After covering up",
  completion: "On completion",
  commissioning: "At commissioning",
  defect: "Defect",
};

export const FACADE_LABEL: Record<FacadeElevation, string> = {
  north: "North elevation",
  "north-east": "North-east elevation",
  east: "East elevation",
  "south-east": "South-east elevation",
  south: "South elevation",
  "south-west": "South-west elevation",
  west: "West elevation",
  "north-west": "North-west elevation",
  roof: "Roof",
  interior: "Interior",
  "not-applicable": "No elevation",
};

/**
 * Every `MeasureCategory`, for the required-photos lookup's picker.
 *
 * A `Record<MeasureCategory, true>` rather than a hand-written array, checked
 * for completeness by the compiler exactly as the API's own
 * `MEASURE_CATEGORIES` is in `apps/api/src/capture/capture-model.ts`: a
 * category added to the union without being added here is a compile error, not
 * a dropdown that is silently one measure short.
 */
export const MEASURE_CATEGORIES: readonly MeasureCategory[] = Object.keys({
  "cavity-wall-insulation": true,
  "external-wall-insulation": true,
  "internal-wall-insulation": true,
  "loft-insulation": true,
  "room-in-roof-insulation": true,
  "flat-roof-insulation": true,
  "pitched-roof-insulation": true,
  "underfloor-insulation": true,
  "park-home-insulation": true,
  "draught-proofing": true,
  "glazing-and-external-doors": true,
  "loft-hatch": true,
  "chimney-sealing": true,
  ventilation: true,
  "heating-controls": true,
  "boiler-replacement": true,
  "heat-pump-air-source": true,
  "heat-pump-ground-source": true,
  "biomass-boiler": true,
  "heat-network-connection": true,
  "solar-photovoltaic": true,
  "solar-thermal": true,
  "battery-storage": true,
  "shading-devices": true,
  other: true,
} satisfies Record<MeasureCategory, true>) as MeasureCategory[];

/** Metres, to one decimal. A tolerance of 3.2 m and one of 32 m are not alike. */
export function metres(value: number): string {
  return `±${value.toFixed(1)} m`;
}

/**
 * A decimal coordinate pair, to six places — about a tenth of a metre, which is
 * finer than any consumer receiver and coarse enough to fit on a line.
 *
 * The stamp already renders degrees-minutes-seconds, which is what an assessor
 * compares against a site plan. This is the machine-readable form beside it, so
 * a reader can paste it into a map without transcribing sexagesimal by hand.
 */
export function coordinates(
  latitude: number | null,
  longitude: number | null,
): string | null {
  if (latitude === null || longitude === null) return null;
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

/**
 * How far the hand-set position is from the one the receiver measured.
 *
 * Both are kept precisely so this can be answered. Null when there is nothing
 * to compare — an override over a photograph that had no fix at all is the
 * worst case and says so in words instead.
 *
 * Equirectangular rather than haversine: the two points are metres apart in the
 * cases that matter, the error over that distance is far below the accuracy of
 * anything being compared, and a formula somebody can check in four lines is
 * worth more here than a fifth decimal place.
 */
export function overrideDistanceM(photo: PhotoEvidence): number | null {
  const override = photo.override;
  if (override === null) return null;
  const { latitude: oLat, longitude: oLon } = override;
  const { latitude: mLat, longitude: mLon } = photo.measured;
  if (oLat === null || oLon === null || mLat === null || mLon === null) {
    return null;
  }
  const metresPerDegree = 111_320;
  const dLat = (oLat - mLat) * metresPerDegree;
  const dLon =
    (oLon - mLon) * metresPerDegree * Math.cos((((oLat + mLat) / 2) * Math.PI) / 180);
  return Math.sqrt(dLat * dLat + dLon * dLon);
}

/** "12 m", "1.4 km" — a distance a reader can picture. */
export function distance(m: number): string {
  if (m < 1000) return `${m.toFixed(m < 10 ? 1 : 0)} m`;
  return `${(m / 1000).toFixed(1)} km`;
}
